import { FormattedMessage, useIntl } from 'react-intl';
import { InputBusStop } from '../Inputs/InputBusStop';
import { InputText } from '../Inputs/InputText';
import { InputPostalCode } from '../Inputs/InputPostalCode';
import { FormElement } from '../FormElement';
import { useFormContext, FieldError } from 'react-hook-form';
import { FormSection } from '../FormSection';
import { cn } from '@/helpers/classnames';
import { SchemaType } from '@/hooks/useStudentFormSchema';

type StudentFormType =
  | SchemaType<'create' | 'update', 'transportation'>
  | SchemaType<'create' | 'update', 'travels'>;

interface StudentSchoolAddressesProps {
  editMode: boolean;
}

export const StudentSchoolAddresses = ({
  editMode,
}: StudentSchoolAddressesProps) => {
  return (
    <FormSection
      title={<FormattedMessage defaultMessage="Skola" />}
      editMode={editMode}
    >
      <div className="flex flex-col gap-2">
        <SchoolFields editMode={editMode} className={cn(editMode && 'mb-2')} />
      </div>
    </FormSection>
  );
};

interface SchoolFieldsProps {
  editMode: boolean;
  className?: string;
}

type SchoolErrors = {
  school_name?: FieldError;
  school_bus_stop?: FieldError;
  school_address?: {
    street?: FieldError;
    zip_code?: FieldError;
    city?: FieldError;
  };
};

const SchoolFields = ({ editMode, className }: SchoolFieldsProps) => {
  const { formatMessage } = useIntl();
  const {
    control,
    formState: { errors },
  } = useFormContext<StudentFormType>();

  const typedErrors = errors as SchoolErrors;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <FormElement
        id="school_name"
        label={<FormattedMessage defaultMessage="Skolans namn" />}
        input={
          <InputText
            name="school_name"
            control={control}
            editMode={editMode}
            error={typedErrors.school_name}
            helperTextReserveSpace
          />
        }
      />
      <FormElement
        id="school_address.street"
        label={<FormattedMessage defaultMessage="Adress" />}
        required={editMode}
        input={
          <InputText
            name={'school_address.street' as never}
            control={control}
            editMode={editMode}
            error={typedErrors.school_address?.street}
            helperTextReserveSpace
          />
        }
      />
      <FormElement
        id="school_address.zip_code"
        label={<FormattedMessage defaultMessage="Postnummer" />}
        required={editMode}
        input={
          <InputPostalCode
            name={'school_address.zip_code' as never}
            control={control}
            editMode={editMode}
            error={typedErrors.school_address?.zip_code}
            helperTextReserveSpace
          />
        }
      />
      <FormElement
        id="school_address.city"
        label={<FormattedMessage defaultMessage="Postort" />}
        required={editMode}
        input={
          <InputText
            name={'school_address.city' as never}
            control={control}
            editMode={editMode}
            error={typedErrors.school_address?.city}
            helperTextReserveSpace
          />
        }
      />
      <FormElement
        id="school_bus_stop"
        label={<FormattedMessage defaultMessage="Hållplats" />}
        input={
          <InputBusStop
            name="school_bus_stop"
            control={control}
            editMode={editMode}
            error={typedErrors.school_bus_stop}
            helperTextReserveSpace
            helperText={formatMessage({
              defaultMessage:
                'Specifikt hållplatsnamn för biljettutställning. Även om adress skulle vara angivet så kommer biljettköp misslyckas om hållplatsen inte finns.',
            })}
          />
        }
      />
    </div>
  );
};
